import { useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
export function useCesiumEventRouter(params) {
    const { viewer, pathsTool, placemarksTool, labelsTool, measureTool, polygonTool, contourHighlight, streamHighlight, placemarkClustering, serverArtifactRenderer, commentPins, interactionEnabled, markupIsActive, isPathsToolActive, isPlacemarkToolActive, isMeasureToolActive, isPolygonToolActive, isLabelToolActive, isCommentToolActive, bufferEnabled, bufferDistance, bufferUnit, bufferError, onSetHoveredDraggable, onSetHoveredPolygonBody, onSetHoveredPlacemark, hoveredPlacemarkEntityRef, } = params;
    // Use ref for buffer config to avoid stale closures in double-click handler
    // without adding buffer values to the dependency array
    const bufferRef = useRef({ bufferEnabled, bufferDistance, bufferUnit, bufferError });
    bufferRef.current = { bufferEnabled, bufferDistance, bufferUnit, bufferError };
    // Every tool hook returns a fresh object literal each render. Depending on
    // those objects directly meant this effect tore down and re-registered all
    // nine Cesium input actions on *every* MapViewer render. Latch them in a ref
    // instead: handlers read the current tools at dispatch time (so they are
    // never stale) and the effect only re-runs when an actual mode flag flips.
    const toolsRef = useRef({
        pathsTool, placemarksTool, labelsTool, measureTool, polygonTool,
        contourHighlight, streamHighlight, placemarkClustering,
        serverArtifactRenderer, commentPins,
        onSetHoveredDraggable, onSetHoveredPolygonBody, onSetHoveredPlacemark,
    });
    toolsRef.current = {
        pathsTool, placemarksTool, labelsTool, measureTool, polygonTool,
        contourHighlight, streamHighlight, placemarkClustering,
        serverArtifactRenderer, commentPins,
        onSetHoveredDraggable, onSetHoveredPolygonBody, onSetHoveredPlacemark,
    };
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const handler = viewer.cesiumWidget.screenSpaceEventHandler;
        // LEFT_CLICK — priority chain (comment-drop wins first when tool is active)
        const onLeftClick = (event) => {
            var _a;
            const { commentPins, pathsTool, placemarksTool, labelsTool, measureTool, polygonTool, placemarkClustering, serverArtifactRenderer, streamHighlight, contourHighlight, } = toolsRef.current;
            if (isCommentToolActive && commentPins.handleMapClick({ position: event.position })) {
                return;
            }
            if (isPathsToolActive && pathsTool.handleMapClick({ position: event.position })) {
                return;
            }
            if (isPlacemarkToolActive && placemarksTool.handleMapClick({ position: event.position })) {
                return;
            }
            if (isLabelToolActive && labelsTool.handleMapClick({ position: event.position })) {
                return;
            }
            if (isMeasureToolActive && measureTool.handleMapClick({ position: event.position })) {
                return;
            }
            // Polygon tool armed for fresh drawing: consume the click so the
            // existing-feature pick handlers below (polygon dialog, server-artifact
            // view with its setTool(null) side effect) can't steal it — the tool's
            // private drawing handler places the vertex. Returns false in
            // edit/select mode, keeping the existing-polygon chain intact.
            if (isPolygonToolActive && polygonTool.handleMapClick({ position: event.position })) {
                return;
            }
            // Existing-entity interaction (select/edit) is only allowed inside a
            // project. In the global view or project-creation flow, other projects'
            // markup and saved artifacts may still be rendered, but clicking them
            // must do nothing.
            if (!interactionEnabled) {
                return;
            }
            if (polygonTool.checkExistingPolygonClick({ position: event.position })) {
                return;
            }
            // Active-node anchor: with a polygon vertex selected (yellow), an empty-
            // terrain click inserts a new vertex right after it in ring order. Runs
            // after checkExistingPolygonClick so vertex-toggle / ghost / outline /
            // polygon-select still win; self-guards on an empty pick + active vertex.
            if (isPolygonToolActive && ((_a = polygonTool.handleActiveAnchorInsert) === null || _a === void 0 ? void 0 : _a.call(polygonTool, { position: event.position }))) {
                return;
            }
            if (pathsTool.checkExistingPathClick({ position: event.position })) {
                return;
            }
            const pickedForCluster = viewer.scene.pick(event.position);
            if ((pickedForCluster === null || pickedForCluster === void 0 ? void 0 : pickedForCluster.id) && placemarkClustering.handleClusterClick(pickedForCluster.id, { x: event.position.x, y: event.position.y })) {
                return;
            }
            if (placemarksTool.checkExistingPlacemarkClick({ position: event.position })) {
                return;
            }
            // Existing-pin clicks are handled by the HTML pin overlays themselves
            // (CommentHtmlPin's onClick fires before any Cesium pick), so no
            // Cesium-side fallback is needed here.
            if (labelsTool.checkExistingLabelClick({ position: event.position })) {
                return;
            }
            if (measureTool.checkExistingMeasurementClick({ position: event.position })) {
                return;
            }
            if (serverArtifactRenderer.checkServerArtifactClick({ position: event.position })) {
                return;
            }
            // Always call stream click — the hook itself gates on Highlight
            // Mode for *creating* a new highlight, but routes clicks on
            // already-highlighted streams into the edit dialog regardless.
            if (streamHighlight.handleMapClick({ position: event.position })) {
                return;
            }
            // Same pattern for contour — the hook owns the mode gate so the
            // edit-existing flow stays available without Highlight Mode.
            contourHighlight.handleMapClick({ position: event.position });
        };
        // LEFT_DOUBLE_CLICK — path finish, label edit, then stream/contour unhighlight
        const onDoubleClick = (event) => {
            const { pathsTool, labelsTool, streamHighlight, contourHighlight } = toolsRef.current;
            if (isPathsToolActive && pathsTool.pendingPath && pathsTool.pendingPath.points.length >= 2) {
                const buf = bufferRef.current;
                const bc = buf.bufferEnabled && buf.bufferDistance && !buf.bufferError
                    ? { distance: parseFloat(buf.bufferDistance), unit: buf.bufferUnit } : undefined;
                pathsTool.finishPath(undefined, bc);
                return;
            }
            // Editing/un-highlighting existing entities is project-scoped.
            if (!interactionEnabled) {
                return;
            }
            if (labelsTool.handleMapDoubleClick({ position: event.position })) {
                return;
            }
            // Double-click un-highlights — always available (matches
            // click-to-edit on existing highlights without Highlight Mode).
            if (streamHighlight.handleMapDoubleClick({ position: event.position })) {
                return;
            }
            contourHighlight.handleMapDoubleClick({ position: event.position });
        };
        // LEFT_DOWN — drag start for 5 tools. Each tool's handler self-gates
        // by entity identity (only starts a drag if the click landed on one
        // of its own draggable entities), so it's safe to run them without
        // requiring `markupIsActive`. This lets users move unsaved items
        // (red-dot pins) and saved items they've opened a dialog for, even
        // when the corresponding markup tool isn't currently armed.
        const onLeftDown = (event) => {
            const { contourHighlight, placemarksTool, labelsTool, pathsTool, polygonTool, measureTool, } = toolsRef.current;
            // Dragging existing entities is only allowed inside a project.
            if (!interactionEnabled) {
                return;
            }
            // Contour partial-highlight handles win first — a handle grab must not
            // be intercepted by markup tools or pan the camera.
            if (contourHighlight.handleMapMouseDown({ position: event.position })) {
                return;
            }
            if (placemarksTool.handleMapMouseDown({ position: event.position })) {
                return;
            }
            if (labelsTool.handleMapMouseDown({ position: event.position })) {
                return;
            }
            if (pathsTool.handleMapMouseDown({ position: event.position })) {
                return;
            }
            if (polygonTool.handleMapMouseDown({ position: event.position })) {
                return;
            }
            if (measureTool.handleMapMouseDown({ position: event.position })) {
                return;
            }
        };
        handler.setInputAction(onLeftDown, Cesium.ScreenSpaceEventType.LEFT_DOWN);
        handler.setInputAction(onLeftClick, Cesium.ScreenSpaceEventType.LEFT_CLICK);
        handler.setInputAction(onDoubleClick, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        // Shift-modified LEFT_DOWN routes to a SEPARATE input action in Cesium —
        // without this, holding Shift swallows the press entirely (the polygon
        // tool's Shift+vertex "uniform scale" never starts and the globe pans
        // instead). Bind the same down handler under the SHIFT modifier so a
        // Shift-held grab fires the identical pipeline. The matching MOUSE_MOVE /
        // LEFT_UP SHIFT bindings are added beside their no-modifier registrations
        // below (those consts aren't in scope yet here).
        handler.setInputAction(onLeftDown, Cesium.ScreenSpaceEventType.LEFT_DOWN, Cesium.KeyboardEventModifier.SHIFT);
        // MOUSE_MOVE — all handlers run (not priority-based)
        const onMouseMove = (event) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            const { placemarksTool, labelsTool, pathsTool, measureTool, polygonTool, contourHighlight, streamHighlight, onSetHoveredDraggable, onSetHoveredPolygonBody, onSetHoveredPlacemark, } = toolsRef.current;
            // Outside a project, other projects' entities are non-interactive, so
            // suppress all hover affordances (editable cursors, placemark tooltips)
            // and clear any hover state left over from when we were in the project.
            if (!interactionEnabled) {
                onSetHoveredDraggable(false);
                onSetHoveredPolygonBody === null || onSetHoveredPolygonBody === void 0 ? void 0 : onSetHoveredPolygonBody(false);
                if (hoveredPlacemarkEntityRef.current) {
                    if (hoveredPlacemarkEntityRef.current.label) {
                        hoveredPlacemarkEntityRef.current.label.show = new Cesium.ConstantProperty(false);
                    }
                    hoveredPlacemarkEntityRef.current = null;
                    onSetHoveredPlacemark(null);
                }
                return;
            }
            placemarksTool.handleMapHover({ endPosition: event.endPosition });
            labelsTool.handleMapHover({ endPosition: event.endPosition });
            pathsTool.handleMapHover({ endPosition: event.endPosition });
            measureTool.handleMapHover({ endPosition: event.endPosition });
            polygonTool.handleMapHover({ endPosition: event.endPosition });
            // Contour and stream hover both always run — the cursor manager
            // needs to know about hovered highlights even when Highlight Mode
            // is off, so existing highlights get the editable-cursor
            // affordance.
            contourHighlight.handleMapHover({ endPosition: event.endPosition });
            streamHighlight.handleMapHover({ endPosition: event.endPosition });
            // Comment-pin hover is owned by CommentHtmlPin (DOM mouseenter/leave),
            // not the Cesium pipeline — nothing to dispatch here.
            // Detect hovering over draggable entities for cursor changes
            if (markupIsActive) {
                const dragPickedObject = viewer.scene.pick(event.endPosition);
                const isDraggableEntity = (dragPickedObject === null || dragPickedObject === void 0 ? void 0 : dragPickedObject.id) && (((_a = placemarksTool.isEntityDraggable) === null || _a === void 0 ? void 0 : _a.call(placemarksTool, dragPickedObject.id)) ||
                    ((_b = labelsTool.isEntityDraggable) === null || _b === void 0 ? void 0 : _b.call(labelsTool, dragPickedObject.id)) ||
                    ((_c = pathsTool.isEntityDraggable) === null || _c === void 0 ? void 0 : _c.call(pathsTool, dragPickedObject.id)) ||
                    ((_d = polygonTool.isEntityDraggable) === null || _d === void 0 ? void 0 : _d.call(polygonTool, dragPickedObject.id)) ||
                    ((_e = measureTool.isEntityDraggable) === null || _e === void 0 ? void 0 : _e.call(measureTool, dragPickedObject.id)));
                onSetHoveredDraggable(!!isDraggableEntity);
                // Polygon body/label specifically gets a move cursor (vs the grab
                // cursor for vertices/handles).
                onSetHoveredPolygonBody === null || onSetHoveredPolygonBody === void 0 ? void 0 : onSetHoveredPolygonBody(!!((dragPickedObject === null || dragPickedObject === void 0 ? void 0 : dragPickedObject.id) && ((_f = polygonTool.isBodyEntity) === null || _f === void 0 ? void 0 : _f.call(polygonTool, dragPickedObject.id))));
            }
            else {
                onSetHoveredDraggable(false);
                onSetHoveredPolygonBody === null || onSetHoveredPolygonBody === void 0 ? void 0 : onSetHoveredPolygonBody(false);
            }
            // Placemark entity hover detection (label show/hide + tooltip state)
            const pickedObject = viewer.scene.pick(event.endPosition);
            if (pickedObject && pickedObject.id && pickedObject.id.properties) {
                const entity = pickedObject.id;
                if (hoveredPlacemarkEntityRef.current !== entity) {
                    if ((_g = hoveredPlacemarkEntityRef.current) === null || _g === void 0 ? void 0 : _g.label) {
                        hoveredPlacemarkEntityRef.current.label.show = new Cesium.ConstantProperty(false);
                    }
                    hoveredPlacemarkEntityRef.current = entity;
                    if (entity.label) {
                        entity.label.show = new Cesium.ConstantProperty(true);
                    }
                    const props = {
                        name: ((_j = (_h = entity.properties) === null || _h === void 0 ? void 0 : _h.name) === null || _j === void 0 ? void 0 : _j.getValue()) || entity.name || 'Placemark',
                        description: ((_l = (_k = entity.properties) === null || _k === void 0 ? void 0 : _k.description) === null || _l === void 0 ? void 0 : _l.getValue()) || '',
                        longitude: ((_o = (_m = entity.properties) === null || _m === void 0 ? void 0 : _m.longitude) === null || _o === void 0 ? void 0 : _o.getValue()) || 0,
                        latitude: ((_q = (_p = entity.properties) === null || _p === void 0 ? void 0 : _p.latitude) === null || _q === void 0 ? void 0 : _q.getValue()) || 0
                    };
                    onSetHoveredPlacemark({
                        entity,
                        screenPosition: event.endPosition,
                        properties: props
                    });
                }
            }
            else {
                if (hoveredPlacemarkEntityRef.current) {
                    if (hoveredPlacemarkEntityRef.current.label) {
                        hoveredPlacemarkEntityRef.current.label.show = new Cesium.ConstantProperty(false);
                    }
                    hoveredPlacemarkEntityRef.current = null;
                    onSetHoveredPlacemark(null);
                }
            }
        };
        handler.setInputAction(onMouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        // Keep dispatching moves while Shift stays held mid-drag (see the SHIFT
        // LEFT_DOWN note above).
        handler.setInputAction(onMouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE, Cesium.KeyboardEventModifier.SHIFT);
        // LEFT_UP — drag end for all tools
        const onLeftUp = () => {
            const { contourHighlight, placemarksTool, labelsTool, pathsTool, measureTool, polygonTool, } = toolsRef.current;
            if (!interactionEnabled)
                return;
            contourHighlight.handleMapMouseUp();
            placemarksTool.handleMapMouseUp();
            labelsTool.handleMapMouseUp();
            pathsTool.handleMapMouseUp();
            measureTool.handleMapMouseUp();
            polygonTool.handleMapMouseUp();
        };
        handler.setInputAction(onLeftUp, Cesium.ScreenSpaceEventType.LEFT_UP);
        // Commit the scale even when the mouse is released with Shift still down.
        handler.setInputAction(onLeftUp, Cesium.ScreenSpaceEventType.LEFT_UP, Cesium.KeyboardEventModifier.SHIFT);
        // RIGHT_CLICK — vertex delete while the polygon/path tools are active.
        // No global context menu: right-clicking empty map is intentionally inert.
        const onRightClick = (event) => {
            var _a, _b;
            const { polygonTool, pathsTool } = toolsRef.current;
            if (!interactionEnabled)
                return;
            if (isPolygonToolActive || isPathsToolActive) {
                const pickedObject = viewer.scene.pick(event.position);
                if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
                    if ((_a = polygonTool.handleRightClickDelete) === null || _a === void 0 ? void 0 : _a.call(polygonTool, { position: event.position }))
                        return;
                    if ((_b = pathsTool.handleRightClickDelete) === null || _b === void 0 ? void 0 : _b.call(pathsTool, { position: event.position }))
                        return;
                }
            }
        };
        handler.setInputAction(onRightClick, Cesium.ScreenSpaceEventType.RIGHT_CLICK);
        return () => {
            if (viewer && !viewer.isDestroyed()) {
                handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOWN);
                handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
                handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
                handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
                handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_UP);
                handler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_CLICK);
                // Shift-modified drag bindings (see setup above).
                handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOWN, Cesium.KeyboardEventModifier.SHIFT);
                handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE, Cesium.KeyboardEventModifier.SHIFT);
                handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_UP, Cesium.KeyboardEventModifier.SHIFT);
            }
        };
        // Only the mode flags the handlers actually branch on. The tool objects
        // themselves come from toolsRef at dispatch time, so they must NOT be
        // listed here — they get a new identity every render and would put the
        // nine input-action registrations back on a per-render churn cycle.
    }, [viewer, interactionEnabled, markupIsActive, isMeasureToolActive, isPathsToolActive, isPlacemarkToolActive, isPolygonToolActive, isLabelToolActive, isCommentToolActive]);
}
